/**
 * The shared simulation. Runs on the server (authoritative) and in the browser
 * (prediction). Both must compute identical results from identical inputs, so:
 *
 * - `stepShip` and `stepShell` take no delta time — they always advance exactly
 *   one `TICK_DT`.
 * - No `Date.now()`, no `Math.random()`, no I/O in this file. Randomness comes in
 *   as an argument (`dispersionRoll`), time as `t`.
 * - Browser-safe: no `@nestjs/*`, no Prisma, no Node built-ins.
 *
 * The server is authoritative. A client sends four axes and a flag, each
 * clamped here: `throttle`, `rudder` in [−1, 1]; `aimBearing` (a direction the
 * turrets *train toward* at a bounded rate — never a turret angle); `aimRange`
 * (the distance the guns are laid for, clamped to what the shell can reach);
 * `fire` 0 | 1 (says *when*, never *where*: the shell leaves along the server's
 * own turret yaw). An axis is a rate or a throttle, never a destination.
 */
import { GROUND_SPEED_KEEP, type IslandLayout, groundHull } from './islands';
import {
  type DamageState,
  activateRepair,
  canRepair,
  cloneDamageState,
  initialDamageState,
  speedFactor as damageSpeedFactor,
} from './damage';
import type { Team } from './protocol';
import { oceanHeightAt } from './sea';
import type { ShipSpec } from './ship-spec';

export const TICK_RATE = 20;
export const TICK_DT = 1 / TICK_RATE;

/**
 * Half-size of the square of sea a match is fought on. It lives here rather
 * than with the wire protocol because `stepShip` enforces it, and the browser
 * predicts against the same wall the server holds. `protocol.ts` re-exports it.
 */
export const ARENA_HALF = 3500;
/** Fraction of way a hull keeps after being stopped by the arena wall. */
const WALL_SPEED_KEEP = 0.25;

/**
 * Keep a stepped hull off the land. `before` is where the hull was at the
 * start of the tick, so the move can be swept to the shore it crossed
 * (`groundHull`) rather than guessed at from where it ended up. The same
 * call on the server and in the client's prediction, after every
 * `stepShip`, so a grounded hull ends the tick in the same place on both.
 * A layout with no islands is a no-op.
 */
export function groundShip(before: ShipState, state: ShipState, spec: ShipSpec, layout: IslandLayout): ShipState {
  if (layout.islands.length === 0) return state;
  const g = groundHull(layout, state.x, state.z, spec.beamM * 0.5 + 6, before.x, before.z);
  if (!g.grounded) return state;
  return { ...state, x: g.x, z: g.z, speed: state.speed * GROUND_SPEED_KEEP };
}

export const SHELL_GRAVITY = 9.81;
/** Fraction of velocity lost per second. Light: the range solve ignores it. */
export const SHELL_DRAG = 0.0006;
/** A shell older than this is gone whatever happened to it. */
export const SHELL_MAX_AGE = 45;
/**
 * How much wider the range axis of the fall-of-shot ellipse is than the
 * lateral one — a real salvo straddles more in range than in deflection, and
 * 2.5:1 is the naval rule of thumb (`spawnShell`).
 */
export const RANGE_DISPERSION_MULT = 2.5;
/** A shot laid at a turret's own maximum reach may not scatter short of this fraction of it. */
export const RANGE_DISPERSION_FLOOR = 0.97;
/** Shells are not tested against the water for their first ticks: the muzzle is above it. */
export const SHELL_WATER_GRACE = 0.1;

export interface TurretState {
  /** Radians relative to the hull; 0 = along the keel toward the bow. */
  yaw: number;
  /** Seconds until this turret can fire again. */
  cooldown: number;
}

/**
 * A torpedo launcher's state. Only a clock: a bank of tubes does not train the
 * way a turret does — the arc either covers the bearing or it does not, and
 * `torpedoMountCanBear` answers that from the hull's heading alone.
 */
export interface TorpedoMountState {
  /** Seconds until this mount can launch again. */
  cooldown: number;
}

export interface ShipState {
  x: number;
  z: number;
  yaw: number;
  /** Signed: negative is astern. */
  speed: number;
  /** Actual rudder deflection in [−1, 1]; eases toward the input. */
  rudder: number;
  throttle: number;
  turrets: TurretState[];
  /** One per `spec.torpedoes` mount; empty on a ship that carries none. */
  torpedoMounts: TorpedoMountState[];
  hp: number;
  alive: boolean;
  /** Fires, flooding, zone saturation and the repair party — see `damage.ts`. */
  damage: DamageState;
}

export interface ShipInput {
  throttle: number;
  rudder: number;
  /** World bearing the guns should train toward, radians. */
  aimBearing: number;
  /** Distance the guns are laid for, metres. */
  aimRange: number;
  fire: 0 | 1;
  /**
   * Launch a torpedo salvo this tick. Momentary, like `fire`, and aimed with
   * the same `aimBearing`: the reticle is the reticle, and a second aim the
   * player had to hold in their head would be a second thing to lose track of.
   */
  fireTorpedoes: 0 | 1;
  /** Call the repair party this tick. Momentary, like `fire`. */
  repair: 0 | 1;
}

export const IDLE_INPUT: Readonly<ShipInput> = {
  throttle: 0,
  rudder: 0,
  aimBearing: 0,
  aimRange: 1000,
  fire: 0,
  fireTorpedoes: 0,
  repair: 0,
};

// ─── Angles ──────────────────────────────────────────────────────────────────

const TAU = Math.PI * 2;

/** Wrap to (−π, π]. */
export function wrapAngle(a: number): number {
  let r = a % TAU;
  if (r > Math.PI) r -= TAU;
  else if (r <= -Math.PI) r += TAU;
  return r;
}

/** Shortest signed rotation from `from` to `to`. */
export function angleDelta(from: number, to: number): number {
  return wrapAngle(to - from);
}

export function lerpAngle(a: number, b: number, t: number): number {
  return wrapAngle(a + angleDelta(a, b) * t);
}

/** Clamp to [−1, 1]; anything that is not a finite number becomes 0. */
export function clampUnit(v: unknown): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return 0;
  return v < -1 ? -1 : v > 1 ? 1 : v;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function finite(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/**
 * The intake clamp. Everything from the wire goes through here before the
 * simulation sees it; nothing a client sends can ask for more than a key can.
 */
export function sanitizeInput(raw: unknown, spec: ShipSpec): ShipInput {
  const r = (raw ?? {}) as Partial<Record<keyof ShipInput, unknown>>;
  const longest = Math.max(...spec.turrets.map((t) => maxShellRange(t.shellSpeed)));
  return {
    throttle: clampUnit(r.throttle),
    rudder: clampUnit(r.rudder),
    aimBearing: wrapAngle(finite(r.aimBearing, 0)),
    aimRange: clamp(finite(r.aimRange, 1000), 50, longest),
    fire: r.fire === 1 || r.fire === true ? 1 : 0,
    fireTorpedoes: r.fireTorpedoes === 1 || r.fireTorpedoes === true ? 1 : 0,
    repair: r.repair === 1 || r.repair === true ? 1 : 0,
  };
}

// ─── Ships ───────────────────────────────────────────────────────────────────

export function spawnShipState(spec: ShipSpec, x: number, z: number, yaw: number): ShipState {
  return {
    x,
    z,
    yaw: wrapAngle(yaw),
    speed: 0,
    rudder: 0,
    throttle: 0,
    turrets: spec.turrets.map((t) => ({ yaw: t.yawLimits.centre, cooldown: 0 })),
    torpedoMounts: (spec.torpedoes ?? []).map(() => ({ cooldown: 0 })),
    hp: spec.hp,
    alive: true,
    damage: initialDamageState(spec),
  };
}

/** Advance a ship by exactly one tick. Pure: returns a new state. */
export function stepShip(state: ShipState, input: ShipInput, spec: ShipSpec): ShipState {
  const dt = TICK_DT;
  if (!state.alive) {
    // A sunk hull drifts to a stop and its guns are silent.
    return {
      ...state,
      speed: state.speed * 0.9,
      turrets: state.turrets.map((t) => ({ yaw: t.yaw, cooldown: Math.max(0, t.cooldown - dt) })),
      torpedoMounts: state.torpedoMounts.map((m) => ({ cooldown: Math.max(0, m.cooldown - dt) })),
    };
  }

  const throttle = clampUnit(input.throttle);
  const rudderIn = clampUnit(input.rudder);

  const rudderStep = spec.rudderResponse * dt;
  const rudder = state.rudder + clamp(rudderIn - state.rudder, -rudderStep, rudderStep);

  // The repair party is a momentary request, resolved here so the client's
  // prediction and the server agree on the tick it took effect.
  const damage = input.repair === 1 && canRepair(state.damage) ? activateRepair(state.damage) : cloneDamageState(state.damage);

  // A flooding hull is slow: the top speed drops, the engines are unchanged.
  const maxSpeed = spec.maxSpeed * damageSpeedFactor(damage);
  const target = throttle >= 0 ? throttle * maxSpeed : throttle * maxSpeed * spec.astern;
  const rate = Math.abs(target) > Math.abs(state.speed) ? spec.accel : spec.decel;
  const speed = state.speed + clamp(target - state.speed, -rate * dt, rate * dt);

  // A ship at rest does not turn: yaw rate scales with speed through the water,
  // and reverses when going astern.
  //
  // The sign is subtracted, not added, and that is not a typo. A heading of
  // `yaw` is the direction `(sin yaw, 0, cos yaw)`, and a renderer with +Y up
  // rotates that *counter-clockwise seen from above* as `yaw` grows — so a
  // heading whose yaw increases swings to **port** on screen. `rudder`
  // positive is starboard helm, the way a wheel is read, so it has to take
  // yaw down. Everything that steers (the helm, the bots) speaks in that
  // sign; nothing else in the file cares, because bearings are absolute.
  const speedFactor = Math.min(1, Math.abs(speed) / spec.maxSpeed);
  const direction = speed > 0 ? 1 : speed < 0 ? -1 : 0;
  const yaw = wrapAngle(state.yaw - rudder * spec.turnRate * speedFactor * direction * dt);

  // The sea is bounded. A hull that reaches the edge stops there rather than
  // sailing out of the match: the position is clamped and the way that drove
  // it into the wall is taken off, so the helm answers again as soon as it
  // points back inside. Done here, in shared code, so the browser's
  // prediction puts the wall in the same place the server does.
  let x = state.x + Math.sin(yaw) * speed * dt;
  let z = state.z + Math.cos(yaw) * speed * dt;
  let bounded = speed;
  if (x < -ARENA_HALF || x > ARENA_HALF || z < -ARENA_HALF || z > ARENA_HALF) {
    x = clamp(x, -ARENA_HALF, ARENA_HALF);
    z = clamp(z, -ARENA_HALF, ARENA_HALF);
    bounded = speed * WALL_SPEED_KEEP;
  }

  const desiredRel = wrapAngle(input.aimBearing - yaw);
  const turrets = state.turrets.map((t, i) => {
    const ts = spec.turrets[i]!;
    const slew = ts.slewRate * dt;
    // Work in offsets from the arc's centre, where the arc is a plain interval
    // and the legal path is the one that never leaves it. A bearing outside
    // the arc trains the turret to the nearer limit.
    const { centre, halfArc } = ts.yawLimits;
    const current = clamp(angleDelta(centre, t.yaw), -halfArc, halfArc);
    const wanted = clamp(angleDelta(centre, desiredRel), -halfArc, halfArc);
    const d = wanted - current;
    return {
      yaw: wrapAngle(centre + current + clamp(d, -slew, slew)),
      cooldown: Math.max(0, t.cooldown - dt),
    };
  });

  const torpedoMounts = state.torpedoMounts.map((m) => ({ cooldown: Math.max(0, m.cooldown - dt) }));

  return { x, z, yaw, speed: bounded, rudder, throttle, turrets, torpedoMounts, hp: state.hp, alive: true, damage };
}

/** Whether world bearing `aimBearing` lies inside a hull-relative arc. */
function bearingInArc(state: ShipState, limits: { centre: number; halfArc: number }, aimBearing: number): boolean {
  const rel = wrapAngle(aimBearing - state.yaw);
  return Math.abs(angleDelta(limits.centre, rel)) <= limits.halfArc + 1e-9;
}

/**
 * How close a barrel has to swing to the aim before it counts as "on target",
 * for a player's held fire order (`arena.ts`) — the same tolerance the bots
 * wait for on their own trigger (`bot-ai.ts`), so a mount still training reads
 * the same to both.
 */
export const TURRET_FIRE_TOLERANCE = 0.03;

/**
 * Whether mount `turretIndex` can point at world bearing `aimBearing` from the
 * ship's current heading — that is, whether the bearing lies inside the
 * mount's arc. A mount that cannot bear does not fire: a shell leaving along a
 * limit would be a shot at nothing, and one spawned toward the aim would have
 * to pass through the ship.
 */
export function turretCanBear(state: ShipState, spec: ShipSpec, turretIndex: number, aimBearing: number): boolean {
  return bearingInArc(state, spec.turrets[turretIndex]!.yawLimits, aimBearing);
}

/**
 * Whether launcher `mountIndex` can throw along world bearing `aimBearing`.
 * Same rule as a turret's, and for the same reason: a fish that left along an
 * arc limit would run somewhere nobody asked it to, and one spawned toward the
 * aim would have to swim through its own ship to get there.
 */
export function torpedoMountCanBear(state: ShipState, spec: ShipSpec, mountIndex: number, aimBearing: number): boolean {
  const mount = spec.torpedoes?.[mountIndex];
  if (!mount) return false;
  return bearingInArc(state, mount.yawLimits, aimBearing);
}

/** Indices of torpedo mounts whose reload has run out. */
export function readyTorpedoMounts(state: ShipState): number[] {
  const out: number[] = [];
  for (let i = 0; i < state.torpedoMounts.length; i++) if (state.torpedoMounts[i]!.cooldown <= 0) out.push(i);
  return out;
}

/** Indices of turrets that may fire this tick. */
export function readyTurrets(state: ShipState): number[] {
  const out: number[] = [];
  for (let i = 0; i < state.turrets.length; i++) if (state.turrets[i]!.cooldown <= 0) out.push(i);
  return out;
}

/** World position of a hull-space point. */
export function hullToWorld(state: ShipState, localX: number, localZ: number): { x: number; z: number } {
  const s = Math.sin(state.yaw);
  const c = Math.cos(state.yaw);
  return { x: state.x + s * localZ + c * localX, z: state.z + c * localZ - s * localX };
}

// ─── Gunnery ─────────────────────────────────────────────────────────────────

/** Flat-earth, no-drag maximum range at 45°. */
export function maxShellRange(shellSpeed: number): number {
  return (shellSpeed * shellSpeed) / SHELL_GRAVITY;
}

/**
 * Low-arc elevation that lands a shell `range` metres away on flat water from
 * a muzzle `height` metres above it, ignoring drag. `null` when the range is
 * beyond reach. With no height the closed form applies; with one, range is
 * monotonic in the low arc so a short bisection finds it.
 */
export function solveElevation(range: number, shellSpeed: number, height = 0): number | null {
  if (range <= 0) return null;
  if (height <= 0) {
    const s = (range * SHELL_GRAVITY) / (shellSpeed * shellSpeed);
    if (s > 1) return null;
    return 0.5 * Math.asin(s);
  }
  const reach = (el: number): number => {
    const vy = shellSpeed * Math.sin(el);
    const t = (vy + Math.sqrt(vy * vy + 2 * SHELL_GRAVITY * height)) / SHELL_GRAVITY;
    return shellSpeed * Math.cos(el) * t;
  };
  let lo = -0.2; // slightly depressed guns reach very short ranges
  let hi = Math.PI / 4;
  if (reach(hi) < range) return null;
  if (reach(lo) > range) return lo;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (reach(mid) < range) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** Seconds a shell fired at `range` stays in the air (no drag). */
export function flightTime(range: number, shellSpeed: number, height = 0): number | null {
  const el = solveElevation(range, shellSpeed, height);
  if (el === null) return null;
  const vy = shellSpeed * Math.sin(el);
  return (vy + Math.sqrt(vy * vy + 2 * SHELL_GRAVITY * height)) / SHELL_GRAVITY;
}

export interface ShellState {
  id: number;
  ownerId: string;
  /** Base damage on a hit, from the turret that fired it; the zone decides the rest. */
  damage: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  age: number;
}

export interface Muzzle {
  x: number;
  y: number;
  z: number;
  /** World bearing of the barrel. */
  yaw: number;
}

/** Muzzle height above the waterline for a spec. */
/**
 * Height of the gun barrels above the waterline: the deck (`hull.height` is
 * measured to the deck plating) plus the barrel axis of the twin turret, which
 * sits about 3 m above its base at the sizes the ships mount it.
 */
export const TURRET_BARREL_AXIS_HEIGHT = 3;

export function muzzleHeight(spec: ShipSpec): number {
  return spec.hull.height + TURRET_BARREL_AXIS_HEIGHT;
}

export function turretMuzzle(state: ShipState, spec: ShipSpec, turretIndex: number): Muzzle {
  const ts = spec.turrets[turretIndex]!;
  const yaw = wrapAngle(state.yaw + state.turrets[turretIndex]!.yaw);
  const pivot = hullToWorld(state, ts.x, ts.z);
  return {
    x: pivot.x + Math.sin(yaw) * ts.barrelLength,
    y: muzzleHeight(spec),
    z: pivot.z + Math.cos(yaw) * ts.barrelLength,
    yaw,
  };
}

/**
 * A shell leaving turret `turretIndex`, laid for `range`. The bearing is the
 * server's own turret yaw plus a dispersion offset from `dispersionRoll` in
 * [0, 1) — 0.5 is dead centre. `rangeRoll`, same range, draws the long/short
 * scatter: the actual fall of shot is perturbed from the laid range by up to
 * `RANGE_DISPERSION_MULT` times the lateral spread, so a salvo falls in an
 * ellipse rather than a line. Ranges beyond reach are clamped to the maximum,
 * and a shot laid at that maximum cannot scatter short of
 * `RANGE_DISPERSION_FLOOR` of it.
 */
export function spawnShell(
  id: number,
  ownerId: string,
  state: ShipState,
  spec: ShipSpec,
  turretIndex: number,
  range: number,
  dispersionRoll = 0.5,
  rangeRoll = 0.5,
): ShellState {
  const ts = spec.turrets[turretIndex]!;
  const m = turretMuzzle(state, spec, turretIndex);
  // The solve ignores drag; a first-order correction lays the guns a little
  // long so the shell still lands where it was asked to.
  const h = muzzleHeight(spec);
  const cap = maxShellRange(ts.shellSpeed) * 0.999;
  const target = Math.min(range, cap);
  const rangeScatter = 1 + (rangeRoll - 0.5) * 2 * RANGE_DISPERSION_MULT * ts.dispersion;
  const scattered = target >= cap ? Math.max(target * rangeScatter, cap * RANGE_DISPERSION_FLOOR) : target * rangeScatter;
  const tof = flightTime(scattered, ts.shellSpeed, h) ?? 0;
  const laid = Math.min(scattered * (1 + SHELL_DRAG * tof), cap);
  const el = solveElevation(laid, ts.shellSpeed, h) ?? Math.PI / 4;
  const bearing = m.yaw + (dispersionRoll - 0.5) * 2 * ts.dispersion;
  const horizontal = Math.cos(el) * ts.shellSpeed;
  return {
    id,
    ownerId,
    damage: ts.shellDamage,
    x: m.x,
    y: m.y,
    z: m.z,
    vx: Math.sin(bearing) * horizontal,
    vy: Math.sin(el) * ts.shellSpeed,
    vz: Math.cos(bearing) * horizontal,
    age: 0,
  };
}

/** Advance a shell by exactly one tick. Pure. */
export function stepShell(s: ShellState): ShellState {
  const dt = TICK_DT;
  const keep = 1 - SHELL_DRAG * dt;
  const vx = s.vx * keep;
  const vy = (s.vy - SHELL_GRAVITY * dt) * keep;
  const vz = s.vz * keep;
  return { ...s, x: s.x + vx * dt, y: s.y + vy * dt, z: s.z + vz * dt, vx, vy, vz, age: s.age + dt };
}

/** True once the shell is at or under the sea surface at time `t`. */
export function shellOnWater(s: ShellState, t: number): boolean {
  if (s.age < SHELL_WATER_GRACE) return false;
  return s.y <= oceanHeightAt(s.x, s.z, t);
}

/** Where a segment entered the hit box, in hull space (see `segmentHullHit`). */
export interface HullHit {
  /** Along the keel, toward the bow. */
  f: number;
  /** To starboard. */
  r: number;
  /** Up from the waterline. */
  y: number;
  /**
   * Fraction of the way along the tested segment, 0…1, that the hit sits at —
   * comparable directly to `LandHit.t` (`islands.ts`) found along the same
   * segment, so a caller can tell whether the round struck the hull before or
   * after it would have struck the shore.
   */
  t: number;
}

/**
 * Where the segment from `a` to `b` enters the ship's hit box, or `null` when
 * it misses. The box is yaw-aligned with the hull, from `−draft` to `+height`
 * around the waterline. Slab test in hull space; the entry point is what the
 * damage model classifies.
 */
export function segmentHullHit(
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
  ship: ShipState,
  spec: ShipSpec,
): HullHit | null {
  const s = Math.sin(ship.yaw);
  const c = Math.cos(ship.yaw);
  const toLocal = (p: { x: number; y: number; z: number }) => {
    const dx = p.x - ship.x;
    const dz = p.z - ship.z;
    return { f: dx * s + dz * c, r: dx * c - dz * s, y: p.y };
  };
  const p0 = toLocal(a);
  const p1 = toLocal(b);
  const box = spec.hull;
  let tMin = 0;
  let tMax = 1;
  const axes: Array<[number, number, number, number]> = [
    [p0.f, p1.f, -box.halfLength, box.halfLength],
    [p0.r, p1.r, -box.halfBeam, box.halfBeam],
    [p0.y, p1.y, -box.draft, box.height],
  ];
  for (const [s0, s1, lo, hi] of axes) {
    const d = s1 - s0;
    if (Math.abs(d) < 1e-9) {
      if (s0 < lo || s0 > hi) return null;
      continue;
    }
    let t0 = (lo - s0) / d;
    let t1 = (hi - s0) / d;
    if (t0 > t1) [t0, t1] = [t1, t0];
    if (t0 > tMin) tMin = t0;
    if (t1 < tMax) tMax = t1;
    if (tMin > tMax) return null;
  }
  return {
    f: p0.f + (p1.f - p0.f) * tMin,
    r: p0.r + (p1.r - p0.r) * tMin,
    y: p0.y + (p1.y - p0.y) * tMin,
    t: tMin,
  };
}

/** Does the segment from `a` to `b` pass through the ship's hit box? */
export function segmentHitsHull(
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
  ship: ShipState,
  spec: ShipSpec,
): boolean {
  return segmentHullHit(a, b, ship, spec) !== null;
}

// ─── Contact ─────────────────────────────────────────────────────────────────
//
// Two hulls cannot occupy the same water. A ship is treated as a capsule lying
// on the surface — a keel segment with the beam as its radius — because a
// circle around a 180 m cruiser would keep a destroyer a beam-and-a-half away
// from its bow, and a box would need a rotation test per pair for a difference
// nobody can see at these speeds.
//
// The resolution is deliberately small: the hulls are pushed apart along the
// contact normal in proportion to their displacement, and the part of each
// ship's way that was driving it into the other is taken off. There is no
// angular impulse — a ship's motion here is one signed number along its
// heading, and inventing a spin the helm cannot answer would fight the
// player. Ramming *hurts*, and that is the arena's business (`RAM_*` there),
// not this file's.

/** Fraction of the closing speed that comes back as bounce. */
const CONTACT_RESTITUTION = 0.15;

/** A hull as a capsule on the water: a keel segment, `radius` wide. */
export function hullCapsule(spec: ShipSpec): { half: number; radius: number } {
  const radius = spec.hull.halfBeam;
  return { half: Math.max(0, spec.hull.halfLength - radius), radius };
}

/**
 * Displacement, near enough: the hull box's volume. Only ever used as a ratio
 * between two ships, so the units do not matter.
 */
export function hullMass(spec: ShipSpec): number {
  return spec.hull.halfLength * spec.hull.halfBeam * spec.hull.draft;
}

export interface ShipContact {
  /** Unit vector in the XZ plane, from the first ship toward the second. */
  nx: number;
  nz: number;
  /** How far the two hulls overlap along that normal, metres. */
  depth: number;
  /** Speed at which they are closing along it, m/s. Never negative. */
  closing: number;
  /** Where they are touching, for the spray and the damage marker. */
  x: number;
  z: number;
}

/** Closest approach between two segments, each given as centre ± half·direction. */
function segmentsClosest(
  ax: number,
  az: number,
  adx: number,
  adz: number,
  aHalf: number,
  bx: number,
  bz: number,
  bdx: number,
  bdz: number,
  bHalf: number,
): { ax: number; az: number; bx: number; bz: number } {
  // Solve for the parameters (s, t) in [−half, +half] minimising the distance
  // between a(s) and b(t). The 2×2 system is singular when the keels are
  // parallel, which is common — two ships in line ahead — so that case falls
  // back to projecting one centre onto the other keel.
  const dx = ax - bx;
  const dz = az - bz;
  const b = adx * bdx + adz * bdz; // both directions are unit-length
  const d = adx * dx + adz * dz;
  const e = bdx * dx + bdz * dz;
  const denom = 1 - b * b;
  let s: number;
  let t: number;
  if (denom > 1e-6) {
    s = clamp((b * e - d) / denom, -aHalf, aHalf);
    t = clamp(b * s + e, -bHalf, bHalf);
    s = clamp(b * t - d, -aHalf, aHalf);
  } else {
    s = clamp(-d, -aHalf, aHalf);
    t = clamp(b * s + e, -bHalf, bHalf);
  }
  return { ax: ax + adx * s, az: az + adz * s, bx: bx + bdx * t, bz: bz + bdz * t };
}

/**
 * Where two hulls are touching, or `null` when they are clear of each other.
 * Order matters only for the sign of the normal, which points from `a` to `b`.
 */
export function shipContact(a: ShipState, aSpec: ShipSpec, b: ShipState, bSpec: ShipSpec): ShipContact | null {
  const ca = hullCapsule(aSpec);
  const cb = hullCapsule(bSpec);
  const reach = ca.radius + cb.radius;
  // Cheap reject first: the centres cannot be further apart than both keels
  // plus both beams and still touch.
  const cdx = b.x - a.x;
  const cdz = b.z - a.z;
  const span = ca.half + cb.half + reach;
  if (cdx * cdx + cdz * cdz > span * span) return null;

  const p = segmentsClosest(
    a.x,
    a.z,
    Math.sin(a.yaw),
    Math.cos(a.yaw),
    ca.half,
    b.x,
    b.z,
    Math.sin(b.yaw),
    Math.cos(b.yaw),
    cb.half,
  );
  let nx = p.bx - p.ax;
  let nz = p.bz - p.az;
  let dist = Math.hypot(nx, nz);
  if (dist >= reach) return null;
  if (dist < 1e-6) {
    // Dead-centre overlap: push along the line of centres, or due north if
    // even that is degenerate. Either way the next tick has a real normal.
    dist = 0;
    const cd = Math.hypot(cdx, cdz);
    if (cd > 1e-6) {
      nx = cdx / cd;
      nz = cdz / cd;
    } else {
      nx = 0;
      nz = 1;
    }
  } else {
    nx /= dist;
    nz /= dist;
  }

  // Each ship's way, resolved onto the normal. Positive `closing` means the
  // gap is still shutting.
  const alongA = Math.sin(a.yaw) * nx + Math.cos(a.yaw) * nz;
  const alongB = Math.sin(b.yaw) * nx + Math.cos(b.yaw) * nz;
  const closing = a.speed * alongA - b.speed * alongB;

  return {
    nx,
    nz,
    depth: reach - dist,
    closing: closing > 0 ? closing : 0,
    x: (p.ax + p.bx) * 0.5,
    z: (p.az + p.bz) * 0.5,
  };
}

/**
 * Part two touching hulls: separate them along the normal and take the closing
 * way off both. Pure — returns the pair of new states.
 */
export function resolveShipContact(
  a: ShipState,
  aSpec: ShipSpec,
  b: ShipState,
  bSpec: ShipSpec,
  contact: ShipContact,
): { a: ShipState; b: ShipState } {
  const ma = hullMass(aSpec);
  const mb = hullMass(bSpec);
  const total = ma + mb;
  // The lighter ship gives way: each moves by the *other's* share of the mass.
  const wa = mb / total;
  const wb = ma / total;

  const push = contact.depth;
  const ax = a.x - contact.nx * push * wa;
  const az = a.z - contact.nz * push * wa;
  const bx = b.x + contact.nx * push * wb;
  const bz = b.z + contact.nz * push * wb;

  let speedA = a.speed;
  let speedB = b.speed;
  if (contact.closing > 0) {
    const alongA = Math.sin(a.yaw) * contact.nx + Math.cos(a.yaw) * contact.nz;
    const alongB = Math.sin(b.yaw) * contact.nx + Math.cos(b.yaw) * contact.nz;
    const impulse = contact.closing * (1 + CONTACT_RESTITUTION);
    // Project the impulse back onto each heading: a hull struck square on the
    // beam loses nothing off its speed, which is right — it is shoved sideways
    // by the separation above, not slowed.
    speedA -= impulse * wa * alongA;
    speedB += impulse * wb * alongB;
  }

  return {
    a: { ...a, x: ax, z: az, speed: speedA },
    b: { ...b, x: bx, z: bz, speed: speedB },
  };
}

// ─── Torpedoes ───────────────────────────────────────────────────────────────
//
// A torpedo is the one weapon in the game that is not a ballistic problem. It
// runs at a fixed depth at a fixed speed until its fuel is gone, so its state
// is two coordinates and a bearing — no height, no gravity, no drag — and the
// whole of its flight is decided at the moment it leaves the tube. That is
// deliberate: a fish nobody can steer is a fish everybody can dodge, and the
// skill on both sides is reading water rather than tracking a target.
//
// It is tested against the same capsule ramming uses (`hullCapsule`) rather
// than the box a shell is tested against. A shell arrives from above and the
// zone it lands in is the whole point of the box; a torpedo arrives along the
// surface, where a box's corners would let it slip past a bow it visibly went
// through. The depth it strikes at is `TORPEDO_RUN_DEPTH`, clamped inside the
// target's own draft so a shallow hull is still hit below its waterline.

/** Metres below the surface a fish runs, and so the depth it strikes at. */
export const TORPEDO_RUN_DEPTH = 4;

export interface TorpedoState {
  id: number;
  x: number;
  z: number;
  /** World bearing it is running on. Fixed for its whole life. */
  yaw: number;
  speed: number;
  /** Metres run so far: against `armingDistance` to arm, against `range` to sink. */
  travelled: number;
  range: number;
  armingDistance: number;
  damage: number;
  ownerId: string;
  team: Team;
}

/**
 * A salvo leaving mount `mountIndex` along `bearing`, fanned across the
 * mount's `spread`. `nextId` is the id of the first fish; the rest follow it.
 *
 * The fan is symmetric about the bearing and evenly spaced, so a three-tube
 * mount throws one down the line of sight and one either side. A single tube
 * would have nothing to fan and goes straight.
 */
export function spawnTorpedoSalvo(
  nextId: number,
  ownerId: string,
  team: Team,
  state: ShipState,
  spec: ShipSpec,
  mountIndex: number,
  bearing: number,
): TorpedoState[] {
  const mount = spec.torpedoes?.[mountIndex];
  if (!mount) return [];
  const tubes = Math.max(1, Math.floor(mount.tubes));
  const origin = hullToWorld(state, mount.x, mount.z);
  const out: TorpedoState[] = [];
  for (let i = 0; i < tubes; i++) {
    const offset = tubes === 1 ? 0 : mount.spread * ((2 * i) / (tubes - 1) - 1);
    const yaw = wrapAngle(bearing + offset);
    out.push({
      id: nextId + i,
      x: origin.x,
      z: origin.z,
      yaw,
      speed: mount.speed,
      travelled: 0,
      range: mount.range,
      armingDistance: mount.armingDistance,
      damage: mount.damage,
      ownerId,
      team,
    });
  }
  return out;
}

export interface TorpedoStep {
  state: TorpedoState;
  /** True once the fuel is gone: the fish sinks, with no explosion. */
  spent: boolean;
}

/** Advance a torpedo by `dt` seconds. Pure. */
export function stepTorpedo(t: TorpedoState, dt: number = TICK_DT): TorpedoStep {
  const run = t.speed * dt;
  const state: TorpedoState = {
    ...t,
    x: t.x + Math.sin(t.yaw) * run,
    z: t.z + Math.cos(t.yaw) * run,
    travelled: t.travelled + run,
  };
  return { state, spent: state.travelled >= state.range };
}

/** True once a fish has run far enough for its warhead to be live. */
export function torpedoArmed(t: TorpedoState): boolean {
  return t.travelled >= t.armingDistance;
}

/**
 * Whether this fish is allowed to detonate on that hull at all.
 *
 * Three refusals, and each is a rule rather than an optimisation: an unarmed
 * warhead is inert, a torpedo never sinks the ship that launched it, and there
 * is no friendly fire — a browser game in which a team-mate can drown you is a
 * game about team-mates rather than about the enemy.
 */
export function torpedoArmedAgainst(t: TorpedoState, targetId: string, targetTeam: Team): boolean {
  if (!torpedoArmed(t)) return false;
  if (targetId === t.ownerId) return false;
  if (targetTeam === t.team) return false;
  return true;
}

/**
 * Where this tick's run, from `a` to `b`, strikes a hull — in hull space, so
 * the damage model can classify it — or `null` when it passes clear.
 *
 * The run is a segment and the hull is a capsule, so the test is the closest
 * approach between two segments against the beam. Swept, not sampled: at
 * 30 m/s a tick covers 1.5 m and nothing is missed, but the same test holds if
 * the fish is ever made faster.
 */
export function torpedoHullHit(
  a: { x: number; z: number },
  b: { x: number; z: number },
  ship: ShipState,
  spec: ShipSpec,
): HullHit | null {
  const capsule = hullCapsule(spec);
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dz);
  const half = len / 2;
  const ux = len > 1e-9 ? dx / len : 0;
  const uz = len > 1e-9 ? dz / len : 1;
  const p = segmentsClosest(
    (a.x + b.x) / 2,
    (a.z + b.z) / 2,
    ux,
    uz,
    half,
    ship.x,
    ship.z,
    Math.sin(ship.yaw),
    Math.cos(ship.yaw),
    capsule.half,
  );
  if (Math.hypot(p.bx - p.ax, p.bz - p.az) > capsule.radius) return null;

  // The point on the keel the fish came closest to, read in hull space. `r` is
  // how far off the centreline the run passed, which is which side it struck.
  const s = Math.sin(ship.yaw);
  const c = Math.cos(ship.yaw);
  const ox = p.ax - ship.x;
  const oz = p.az - ship.z;
  // `p.ax, p.az` sits on the fish's own segment (`segmentsClosest` clamps
  // there); project it back onto `a`→`b` for the fraction along the run, the
  // same basis a land strike on this segment is measured in.
  const lenSq = dx * dx + dz * dz;
  const t = lenSq > 1e-9 ? clamp(((p.ax - a.x) * dx + (p.az - a.z) * dz) / lenSq, 0, 1) : 0;
  return {
    f: ox * s + oz * c,
    r: ox * c - oz * s,
    y: -Math.min(TORPEDO_RUN_DEPTH, spec.hull.draft * 0.8),
    t,
  };
}
